<script setup lang="ts">
import {
  Activity,
  CircleCheck,
  Clock3,
  Coins,
  LoaderCircle,
  RefreshCw,
} from "lucide-vue-next"
import { computed, onMounted, ref } from "vue"

import AppSelect from "../components/AppSelect.vue"
import { apiRequest } from "../lib/api"
import { formatShanghaiTime } from "../lib/datetime"
import type { UsageItem, UsageList, UsageSummary } from "../lib/types"
import { useApiKeysStore } from "../stores/apiKeys"
import { useSessionStore } from "../stores/session"

const session = useSessionStore()
const apiKeys = useApiKeysStore()
const calls = ref<UsageItem[]>([])
const summary = ref<UsageSummary | null>(null)
const total = ref(0)
const page = ref(1)
const pageSize = 50
const loading = ref(true)
const error = ref("")
const hours = ref("24")
const platform = ref("")
const status = ref("")
const apiKeyId = ref("")
const timeOptions = [
  { value: "24", label: "最近 24 小时" },
  { value: "168", label: "最近 7 天" },
  { value: "720", label: "最近 30 天" },
  { value: "2160", label: "最近 90 天" },
]
const platformOptions = [
  { value: "", label: "全部平台" },
  { value: "rednote", label: "小红书" },
  { value: "zhihu", label: "知乎" },
  { value: "tophub", label: "TopHub" },
  { value: "douban", label: "豆瓣" },
  { value: "weibo", label: "微博 CLI" },
  { value: "weibo_web", label: "微博 Web" },
  { value: "smzdm", label: "什么值得买" },
  { value: "bilibili", label: "哔哩哔哩" },
  { value: "douyin", label: "抖音" },
  { value: "v2ex", label: "V2EX" },
  { value: "xueqiu", label: "雪球" },
  { value: "reddit", label: "Reddit" },
  { value: "twitter", label: "Twitter / X" },
  { value: "xianyu", label: "闲鱼" },
  { value: "jd", label: "京东" },
  { value: "1688", label: "1688" },
  { value: "linuxdo", label: "Linux.do" },
  { value: "nowcoder", label: "牛客" },
  { value: "instagram", label: "Instagram" },
  { value: "maimai", label: "脉脉" },
  { value: "jianyu", label: "剑鱼标讯" },
  { value: "toutiao", label: "今日头条" },
  { value: "sinafinance", label: "新浪财经" },
  { value: "juejin", label: "掘金" },
  { value: "36kr", label: "36氪" },
  { value: "dongchedi", label: "懂车帝" },
  { value: "ctrip", label: "携程" },
  { value: "boss", label: "BOSS 直聘" },
  { value: "linkedin", label: "LinkedIn" },
]
const statusOptions = [
  { value: "", label: "全部状态" },
  { value: "succeeded", label: "成功" },
  { value: "failed", label: "失败" },
  { value: "running", label: "执行中" },
]
const apiKeyOptions = computed(() => [
  { value: "", label: "全部 API Keys" },
  ...apiKeys.items.map((key) => ({
    value: key.id,
    label: `${key.name} · ••••${key.last_four}`,
  })),
])

const successRate = computed(() => {
  if (!summary.value?.total_calls) return "0%"
  return `${Math.round((summary.value.succeeded_calls / summary.value.total_calls) * 100)}%`
})
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / pageSize)))

onMounted(async () => {
  await Promise.all([apiKeys.load(), load()])
})

async function load(resetPage = false): Promise<void> {
  if (resetPage) page.value = 1
  const workspace = session.workspace
  if (!workspace) {
    loading.value = false
    error.value = "当前账户没有可用的 Workspace。"
    return
  }
  loading.value = true
  error.value = ""
  const params = new URLSearchParams({
    hours: hours.value,
    page: String(page.value),
    page_size: String(pageSize),
  })
  if (platform.value) params.set("platform", platform.value)
  if (status.value) params.set("status", status.value)
  if (apiKeyId.value) params.set("api_key_id", apiKeyId.value)
  try {
    const result = await apiRequest<UsageList>(
      `/v1/workspaces/${workspace.id}/usage?${params}`,
    )
    calls.value = result.items
    summary.value = result.summary
    total.value = result.total
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "加载调用记录失败"
  } finally {
    loading.value = false
  }
}

async function changePage(next: number): Promise<void> {
  page.value = Math.min(Math.max(1, next), totalPages.value)
  await load()
}

function formatDate(value: string): string {
  return formatShanghaiTime(value, {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
}

function formatDuration(value: number | null): string {
  if (value === null) return "—"
  return value >= 1000 ? `${(value / 1000).toFixed(2)}s` : `${value}ms`
}

function statusLabel(value: UsageItem["status"]): string {
  return { succeeded: "成功", failed: "失败", running: "执行中" }[value]
}

function platformLabel(value: UsageItem["platform"]): string {
  return { rednote: "小红书", zhihu: "知乎", tophub: "TopHub", douban: "豆瓣", douyin: "抖音", weibo: "微博 CLI", weibo_web: "微博 Web", smzdm: "什么值得买", bilibili: "哔哩哔哩", v2ex: "V2EX", xueqiu: "雪球", reddit: "Reddit", twitter: "Twitter / X", xianyu: "闲鱼", jd: "京东", "1688": "1688", linuxdo: "Linux.do", nowcoder: "牛客", instagram: "Instagram", maimai: "脉脉", jianyu: "剑鱼标讯", toutiao: "今日头条", sinafinance: "新浪财经", juejin: "掘金", "36kr": "36氪", dongchedi: "懂车帝", ctrip: "携程", boss: "BOSS 直聘", linkedin: "LinkedIn" }[value]
}
</script>

<template>
  <section data-screen-label="Usage">
    <div class="section-heading section-heading--page">
      <div class="page-heading">
        <span class="eyebrow">Usage & billing</span>
        <h1>调用与消耗</h1>
        <p>查看当前 Workspace 的 API 调用、积分消耗和执行结果。</p>
      </div>
      <button class="secondary-button" :disabled="loading" @click="load()">
        <RefreshCw :class="{ spin: loading }" :size="15" />刷新
      </button>
    </div>

    <section class="metrics-strip usage-metrics">
      <article>
        <span class="metric-icon"><Activity :size="17" /></span>
        <div><span>调用次数</span><strong>{{ summary?.total_calls.toLocaleString() ?? "—" }}</strong><small>{{ summary?.failed_calls ?? 0 }} 次失败 · {{ summary?.running_calls ?? 0 }} 次执行中</small></div>
      </article>
      <article>
        <span class="metric-icon"><CircleCheck :size="17" /></span>
        <div><span>成功率</span><strong>{{ successRate }}</strong><small>{{ summary?.succeeded_calls ?? 0 }} 次成功</small></div>
      </article>
      <article>
        <span class="metric-icon"><Coins :size="17" /></span>
        <div><span>积分消耗</span><strong>{{ summary?.total_credits.toLocaleString() ?? "—" }}</strong><small>返回 {{ summary?.total_items.toLocaleString() ?? 0 }} 条内容</small></div>
      </article>
      <article>
        <span class="metric-icon"><Clock3 :size="17" /></span>
        <div><span>平均耗时</span><strong>{{ formatDuration(summary?.avg_duration_ms ?? null) }}</strong><small>实际完成的上游请求耗时</small></div>
      </article>
    </section>

    <div class="usage-filters">
      <label><span>时间范围</span><AppSelect v-model="hours" compact aria-label="时间范围" :options="timeOptions" @change="load(true)" /></label>
      <label><span>API Key</span><AppSelect v-model="apiKeyId" compact aria-label="API Key" :options="apiKeyOptions" @change="load(true)" /></label>
      <label><span>平台</span><AppSelect v-model="platform" compact aria-label="平台" :options="platformOptions" @change="load(true)" /></label>
      <label><span>状态</span><AppSelect v-model="status" compact aria-label="状态" :options="statusOptions" @change="load(true)" /></label>
    </div>

    <p v-if="error" class="form-error usage-error-banner">{{ error }}</p>
    <div class="table-shell usage-table-shell">
      <div class="table-toolbar"><span>{{ total.toLocaleString() }} 次调用</span><span class="eyebrow">WORKSPACE ONLY</span></div>
      <div v-if="loading" class="empty-state"><LoaderCircle class="spin" :size="20" /><span>正在加载调用记录</span></div>
      <div v-else-if="calls.length === 0" class="empty-state"><Activity :size="24" /><strong>暂无调用记录</strong><p>调整筛选范围，或先使用 API Key 发起一次调用。</p></div>
      <div v-else class="responsive-table">
        <table class="usage-table">
          <thead><tr><th>时间（上海）</th><th>API Key</th><th>接口</th><th>平台</th><th>结果</th><th>消耗 / 性能</th></tr></thead>
          <tbody>
            <tr v-for="call in calls" :key="call.request_id">
              <td><strong class="usage-time">{{ formatDate(call.received_at) }}</strong><small>{{ call.protocol.toUpperCase() }}</small></td>
              <td><strong>{{ call.api_key_name }}</strong><small>••••{{ call.api_key_last_four }}</small></td>
              <td><code>{{ call.operation }}</code><small>{{ call.request_id }}</small></td>
              <td><span class="platform-label" :class="`platform-label--${call.platform}`">{{ platformLabel(call.platform) }}</span></td>
              <td><span class="status-pill" :class="{ 'status-pill--active': call.status === 'succeeded', 'status-pill--expired': call.status === 'failed' }"><i></i>{{ statusLabel(call.status) }}</span><small v-if="call.error_code" class="usage-call-error">{{ call.error_code }}</small><small v-else-if="call.http_status">HTTP {{ call.http_status }}</small></td>
              <td><strong class="usage-cost">{{ call.credits }} credits</strong><small>{{ formatDuration(call.duration_ms) }} · {{ call.item_count ?? 0 }} items</small></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <footer v-if="total > pageSize" class="usage-pagination">
      <span>第 {{ page }} / {{ totalPages }} 页</span>
      <div><button class="secondary-button" :disabled="page <= 1" @click="changePage(page - 1)">上一页</button><button class="secondary-button" :disabled="page >= totalPages" @click="changePage(page + 1)">下一页</button></div>
    </footer>
  </section>
</template>

<style scoped>
.usage-metrics { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.usage-filters { display: grid; grid-template-columns: repeat(4, minmax(120px, 1fr)); gap: 10px; margin-top: 22px; padding: 14px; border: 1px solid var(--line); background: var(--panel-muted); }
.usage-filters label { min-width: 0; display: grid; gap: 7px; }
.usage-filters label > span { color: var(--muted); font-size: 9px; font-weight: 650; letter-spacing: .05em; text-transform: uppercase; }
.usage-filters select {
  width: 100%;
  min-width: 0;
  height: 40px;
  padding: 0 40px 0 13px;
  appearance: none;
  border: 1px solid var(--line-strong);
  border-radius: 9px;
  color: var(--ink);
  background-color: var(--panel);
  background-image:
    linear-gradient(45deg, transparent 50%, var(--muted) 50%),
    linear-gradient(135deg, var(--muted) 50%, transparent 50%);
  background-position: calc(100% - 17px) 50%, calc(100% - 12px) 50%;
  background-repeat: no-repeat;
  background-size: 5px 5px;
  cursor: pointer;
  font-size: 11px;
  outline: 0;
  transition: border-color 140ms ease, box-shadow 140ms ease, background-color 140ms ease;
}
.usage-filters select:hover { border-color: color-mix(in oklch, var(--ink) 52%, var(--line-strong)); }
.usage-filters select:focus { border-color: var(--accent); box-shadow: 0 0 0 3px color-mix(in oklch, var(--accent) 13%, transparent); }
.usage-error-banner { margin-top: 16px; }
.usage-table-shell { margin-top: 16px; }
.usage-table { min-width: 940px; }
.usage-table td { height: 75px; }
.usage-table td > small { display: block; max-width: 230px; margin-top: 5px; overflow: hidden; color: var(--muted); font-size: 9px; text-overflow: ellipsis; white-space: nowrap; }
.usage-time, .usage-cost { font: 600 10px monospace; white-space: nowrap; }
.usage-table td code { font-size: 10px; }
.platform-label { display: inline-flex; padding: 5px 8px; font: 650 9px monospace; }
.platform-label--rednote { color: var(--accent); background: var(--accent-soft); }
.platform-label--zhihu { color: #075ca8; background: #e8f3ff; }
.platform-label--tophub { color: #9b2c2c; background: #fff0f0; }
.platform-label--douban { color: #26734d; background: #edf8f1; }
.platform-label--weibo { color: #a04d00; background: #fff4e8; }
.platform-label--weibo_web { color: #9b3a24; background: #fff0ec; }
.platform-label--smzdm { color: #9c2533; background: #fff0f2; }
.platform-label--bilibili { color: #0784b5; background: #edfaff; }
.platform-label--jd { color: #b21f2d; background: #fff0f2; }
.platform-label--xianyu { color: #b56a00; background: #fff7df; }
.platform-label--1688 { color: #b84f00; background: #fff3e8; }
.platform-label--linuxdo { color: #175f4a; background: #eaf8f1; }
.platform-label--nowcoder { color: #006f5f; background: #e8faf5; }
.platform-label--instagram { color: #9f276f; background: #fff0f8; }
.platform-label--maimai { color: #1165b0; background: #edf6ff; }
.platform-label--jianyu { color: #8a4b08; background: #fff5df; }
.usage-call-error { color: var(--accent) !important; }
.usage-pagination { display: flex; align-items: center; justify-content: space-between; gap: 18px; margin-top: 16px; color: var(--muted); font-size: 10px; }
.usage-pagination > div { display: flex; gap: 8px; }

@media (max-width: 980px) {
  .usage-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .usage-metrics article:nth-child(2) { border-right: 0; }
  .usage-metrics article:nth-child(-n + 2) { border-bottom: 1px solid var(--line); }
  .usage-filters { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 600px) {
  .usage-metrics, .usage-filters { grid-template-columns: 1fr; }
  .usage-metrics article { border-right: 0; border-bottom: 1px solid var(--line); }
  .usage-metrics article:last-child { border-bottom: 0; }
  .usage-pagination { align-items: stretch; flex-direction: column; }
}
</style>
